import {
  AICLAIMS_WELCOME_PATH,
  isAiClaimsPortalPath,
} from '../config/aiclaims-routes'
import { sanitizeInternalRedirect } from '../utils/safeRedirect'
import { parseHostnameFromEnv } from './brandFromHost'
import { isWysehubAdminHost, isWysehubPortalHost } from './hostRouting'
import {
  inferAiClaimsPortalHostname,
  inferWysehubPortalHostname,
  isAiClaimsPortalHostname,
} from './managedHosts'
import { canUseAiClaimsProduct } from './pageAccess'

export function getAiClaimsPortalHostname(currentHost?: string): string {
  if (currentHost) {
    return inferAiClaimsPortalHostname(currentHost)
  }
  if (typeof window !== 'undefined') {
    return inferAiClaimsPortalHostname(window.location.hostname)
  }
  return parseHostnameFromEnv(process.env.NEXT_PUBLIC_AICLAIMS_PORTAL_HOST)
}

function getTargetPortalHostname(
  targetBrand: 'wysehub' | 'aiclaims',
  currentHost: string,
): string {
  return targetBrand === 'aiclaims'
    ? inferAiClaimsPortalHostname(currentHost)
    : inferWysehubPortalHostname(currentHost)
}

export function shouldUseSessionHandoffForHost(
  targetBrand: 'wysehub' | 'aiclaims',
  currentHost: string,
): boolean {
  const host = currentHost.toLowerCase()
  const targetHost = getTargetPortalHostname(targetBrand, host)
  if (!targetHost) {
    return false
  }

  return host !== targetHost
}

export function shouldUseSessionHandoff(
  targetBrand: 'wysehub' | 'aiclaims',
): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  return shouldUseSessionHandoffForHost(targetBrand, window.location.hostname)
}

export function buildSessionHandoffUrl(
  brand: 'wysehub' | 'aiclaims',
  nextPath: string,
): string {
  const defaultNext = brand === 'aiclaims' ? AICLAIMS_WELCOME_PATH : '/homehub'
  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
  const safeNext = sanitizeInternalRedirect(nextPath, origin) ?? defaultNext
  const params = new URLSearchParams({ brand, next: safeNext })
  return `/api/v1/login/session-handoff/start?${params.toString()}`
}

export function navigateToBrandPortal(
  brand: 'wysehub' | 'aiclaims',
  nextPath: string,
  sameHostNavigate: () => void,
): void {
  if (shouldUseSessionHandoff(brand)) {
    window.location.href = buildSessionHandoffUrl(brand, nextPath)
    return
  }

  sameHostNavigate()
}

export function resolveAllowedHomePath(
  brandAccess?: string | null,
): '/homehub' | typeof AICLAIMS_WELCOME_PATH {
  if (brandAccess === 'aiclaims') {
    return AICLAIMS_WELCOME_PATH
  }
  return '/homehub'
}

function isAiClaimsPortalHost(hostname: string): boolean {
  return (
    isAiClaimsPortalHostname(hostname) ||
    hostname.toLowerCase() ===
      parseHostnameFromEnv(
        process.env.NEXT_PUBLIC_AICLAIMS_PORTAL_HOST,
      ).toLowerCase()
  )
}

/** True when a single-brand user landed on /unauthorised on the wrong product portal. */
export function shouldRedirectFromUnauthorisedPage(
  brandAccess: string | null | undefined,
  hostname: string,
): boolean {
  if (!brandAccess || brandAccess === 'both') {
    return false
  }

  const onAiClaimsPortal = isAiClaimsPortalHost(hostname)

  if (brandAccess === 'aiclaims') {
    return !onAiClaimsPortal
  }

  if (brandAccess === 'wysehub') {
    return onAiClaimsPortal
  }

  return false
}

/** Default post-login route for the current portal host and user brand_access. */
export function resolvePostLoginPath(
  brandAccess: string | null | undefined,
  hostname: string,
): string {
  if (isWysehubAdminHost(hostname)) {
    return '/admin'
  }

  if (isAiClaimsPortalHost(hostname)) {
    if (brandAccess === 'wysehub') {
      return '/homehub'
    }
    if (canUseAiClaimsProduct(brandAccess)) {
      return AICLAIMS_WELCOME_PATH
    }
    return '/'
  }

  if (isWysehubPortalHost(hostname)) {
    if (brandAccess === 'aiclaims') {
      return AICLAIMS_WELCOME_PATH
    }
    return '/homehub'
  }

  // Host env may be missing or stale in the client bundle; still honour brand_access
  // so AiClaims-only users are never dumped onto /homehub by default.
  return resolveAllowedHomePath(brandAccess)
}

export function navigateToAllowedBrandHome(
  brandAccess: string | null | undefined,
  nextPath?: string,
): void {
  if (typeof window === 'undefined') {
    return
  }

  const path =
    nextPath ?? resolvePostLoginPath(brandAccess, window.location.hostname)

  if (isAiClaimsPortalPath(path)) {
    navigateToBrandPortal('aiclaims', path, () => {
      window.location.href = path
    })
    return
  }

  if (path === '/homehub' || path.startsWith('/homehub/')) {
    navigateToBrandPortal('wysehub', path, () => {
      window.location.href = path
    })
    return
  }

  window.location.href = path
}
